import { useGetCommunityMembersQuery, useUpdateCommunityParticipantMutation } from '@/api/communitiesApi';
import {
  Block,
  SettingSectionTitle,
  SettingsFlexbox,
  ShrinkableSettings
} from '@/components/DybrComponents/StyledServicePagesElements';
import SearchTextField from '@/components/FormElements/SearchTextField';
import Select from '@/components/FormElements/Select';
import { Table } from '@/components/ui/shared/Table';
import { ACCESS_LEVELS, getAccessLevelRank } from '@/scenes/Management/Community/membershipRolesUtil';
import css from '@/scenes/Management/Community/CommunityMembers.module.css';
import { useActiveProfile } from '@/store/localStorage/useTokenState';
import { selectActiveProfileAugmented } from '@/store/user/selectors/selectActiveProfileAugmented';
import { classNames } from '@/utils/helpers/classNames';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 10;

const ACCESS_LEVEL_OPTIONS = ACCESS_LEVELS.map((level, rank) => ({
  value: rank,
  label: level.label
}));

const STRINGS = {
  title: 'Участники сообщества',
  description:
    'Вы можете просмотреть список участников сообщества и управлять их правами доступа (администрирование, публикация записей и комментирование).',
  loading: 'Загрузка...',
  empty: 'В сообществе пока нет участников',
  loadError: 'Не удалось загрузить список участников',
  toggleError: nickname => `Не удалось изменить права для ${nickname}`,
  searchLabel: 'искать по нику',
  noSearchResults: 'Участники с таким ником не найдены',
  columns: {
    participant: 'участник',
    accessLevel: 'права доступа'
  }
};

export function CommunityMembers() {
  const { activeProfileId } = useActiveProfile();
  const profile = useSelector(state => selectActiveProfileAugmented(state, { activeProfileId }));
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();

  const {
    data: members = [],
    isLoading,
    isError
  } = useGetCommunityMembersQuery({ communityId: profile.id }, { skip: !profile.id });
  const [updateCommunityParticipant] = useUpdateCommunityParticipantMutation();

  const loading = Boolean(profile.id) && isLoading;

  const filteredMembers = members.filter(member => {
    const nickname = member.profile?.nickname ?? '';
    return nickname.toLowerCase().includes(search.trim().toLowerCase());
  });

  const totalRecords = filteredMembers.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const currentPage = Math.min(Number(searchParams.get('page') || '1'), totalPages);
  const pagedMembers = filteredMembers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleAccessLevelChange = async (member, rank) => {
    const newPermissions = ACCESS_LEVELS[rank].permissions;

    try {
      await updateCommunityParticipant({
        communityId: profile.id,
        participantId: member.id,
        permissions: newPermissions
      }).unwrap();
    } catch {
      alert(STRINGS.toggleError(member.profile?.nickname));
    }
  };

  return (
    <Block>
      <SettingsFlexbox>
        <SettingSectionTitle>
          <h4>{STRINGS.title}</h4>
          <p>{STRINGS.description}</p>
        </SettingSectionTitle>
        <ShrinkableSettings>
          {loading ? (
            <div className={css.message}>{STRINGS.loading}</div>
          ) : isError ? (
            <div
              className={classNames({
                [css.message]: true,
                [css.error]: true
              })}
            >
              {STRINGS.loadError}
            </div>
          ) : members.length > 0 ? (
            <>
              <SearchTextField
                className={css.search}
                value={search}
                onChange={e => setSearch(e.target.value)}
                touched
                label={STRINGS.searchLabel}
              />
              {pagedMembers.length > 0 ? (
                <Table pagination={{ totalRecords, currentPage, pageSize: PAGE_SIZE }}>
                  <Table.Head>
                    <Table.HeaderCell>{STRINGS.columns.participant}</Table.HeaderCell>
                    <Table.HeaderCell className={css.accessLevelColumn}>
                      {STRINGS.columns.accessLevel}
                    </Table.HeaderCell>
                  </Table.Head>
                  <Table.Body>
                    {pagedMembers.map(member => {
                      const accessLevelRank = getAccessLevelRank(member.permissions || []);

                      return (
                        <Table.Row key={member.id} rowId={member.id}>
                          <Table.ProfileCell profile={member.profile} />
                          <Table.Cell className={css.accessLevelColumn}>
                            <div className={css.accessLevelCell}>
                              <Select
                                className={css.accessLevelSelect}
                                options={ACCESS_LEVEL_OPTIONS}
                                selectedOptionID={accessLevelRank}
                                menuPortalTarget={document.body}
                                onChange={rank => handleAccessLevelChange(member, rank)}
                              />
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              ) : (
                <div className={css.message}>{STRINGS.noSearchResults}</div>
              )}
            </>
          ) : (
            <div className={css.message}>{STRINGS.empty}</div>
          )}
        </ShrinkableSettings>
      </SettingsFlexbox>
    </Block>
  );
}
